import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { DataFetcher } from '@provider-portal/common/api/apiFetcher/createFetcher';
import {
  GetAssignedPromotionsRequest,
  GetAssignedPromotionsResponse,
  SelfServiceCampaignAction,
  SelfServicePromotionDetailsResponse,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { toTimestamp } from '@provider-portal/common/utils/date';

import { IntlShape } from 'react-intl/src/types';

import { PromotionDetails } from '../types';

export interface ListPromotionsState {
  active: PromotionDetails[];
  ended: PromotionDetails[];
  lastUpdate: Date;
  isUpdatedByOrder: boolean;
}

const ONE_SECOND_IN_MS = 1000;

const mapPromotion = (
  promotion: SelfServicePromotionDetailsResponse,
  intl: IntlShape,
): PromotionDetails => {
  return {
    id: promotion.id,
    promotionName: promotion.title.value,
    startDate: promotion.start_timestamp * ONE_SECOND_IN_MS,
    endDate: promotion.end_timestamp * ONE_SECOND_IN_MS,
    numberCustomers: promotion.customers ? promotion.customers.toString() : null,
    numberOrders: promotion.orders ? promotion.orders.toString() : null,
    sales: promotion.net_orders_value
      ? intl.formatNumber(promotion.net_orders_value, {
          currency: promotion.currency,
          style: 'currency',
        })
      : null,
    totalSpend: promotion.total_spend
      ? intl.formatNumber(promotion.total_spend, {
          currency: promotion.currency,
          style: 'currency',
        })
      : null,
    newUsers: promotion.new_users ? promotion.new_users.toString() : null,
    gmvImpact: promotion.gmv_impact
      ? intl.formatNumber(promotion.gmv_impact, {
          currency: promotion.currency,
          style: 'currency',
        })
      : null,
    bannedUntil: promotion.banned_until ? promotion.banned_until * ONE_SECOND_IN_MS : null,
    isBestUserCampaign: promotion.is_best_user_campaign,
  };
};

export function usePromotionsLoader(
  providerId: number,
  listFetcher: DataFetcher<GetAssignedPromotionsResponse, [request: GetAssignedPromotionsRequest]>,
  filterOutUpcommingPromotions = false,
): ListPromotionsState | null {
  const intl = useIntl();

  const [promotions, setPromotions] = useState<ListPromotionsState | null>(null);

  useEffect(() => {
    listFetcher.fetch({ provider_id: providerId }).then((data) => {
      if (!data) {
        return;
      }
      const now = toTimestamp(new Date());
      const newState = data.campaigns.reduce<
        Omit<ListPromotionsState, 'lastUpdate'> & { lastUpdate: number }
      >(
        (acc, promotion) => {
          if (
            (promotion.action === SelfServiceCampaignAction.ASSIGNED ||
              promotion.action === SelfServiceCampaignAction.FOOD_BANNED) &&
            promotion.end_timestamp > now
          ) {
            if (!filterOutUpcommingPromotions || now >= promotion.start_timestamp) {
              acc.active.push(mapPromotion(promotion, intl));
            }
          } else {
            acc.ended.push(mapPromotion(promotion, intl));
          }
          if (promotion.orders > 0) {
            acc.lastUpdate = Math.max(acc.lastUpdate, promotion.last_update);
            acc.isUpdatedByOrder = true;
          }
          return acc;
        },
        { active: [], ended: [], lastUpdate: 0, isUpdatedByOrder: false },
      );

      newState.ended.sort((a, b) => {
        return b.endDate - a.endDate;
      });

      setPromotions({ ...newState, lastUpdate: new Date(newState.lastUpdate * ONE_SECOND_IN_MS) });
    });
  }, [intl, listFetcher, providerId, filterOutUpcommingPromotions]);
  return promotions;
}
